import { useState, type ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { Pencil, PlusIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useParamOrThrow } from "@/shared/hooks/use-param-or-throw";
import { useUpdateTemplateHeader } from "../hooks/use-update-template-header";
import { useDeleteTemplate } from "../hooks/use-delete-template";
import {
  DetailHeaderActions,
  DetailHeaderInfo,
} from "@/shared/components/detail-header";

export type TemplateDetailsHeaderProps = {
  name?: string;
  context?: string | null;
  createdAt?: string;
  updatedAt?: string;
  isReadOnly?: boolean;
  isCreatingReport?: boolean;
  onCreateReport?: () => void;
  createReportTrigger?: ReactNode;
};

export function TemplateDetailsHeader({
  name,
  context,
  createdAt,
  updatedAt,
  isReadOnly = false,
  isCreatingReport = false,
  onCreateReport,
  createReportTrigger,
}: TemplateDetailsHeaderProps) {
  const uuid = useParamOrThrow("uuid");

  const { mutateAsync: updateHeaderAsync, isPending: isSaving } =
    useUpdateTemplateHeader();
  const { mutate: deleteTemplate, isPending: isDeletingTemplate } =
    useDeleteTemplate(uuid);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(name ?? "");
  const [subtitle, setSubtitle] = useState(context ?? "");
  const [saveError, setSaveError] = useState<string>();

  const onOpenChange = (open: boolean) => {
    setTitle(name ?? "");
    setSubtitle(context ?? "");
    setSaveError(undefined);
    setOpen(open);
  };

  const handleSave = async () => {
    setSaveError(undefined);
    try {
      await updateHeaderAsync({
        templateId: uuid,
        name: title.trim() || name,
        context: subtitle.trim() || null,
      });
      setOpen(false);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save template",
      );
    }
  };

  return (
    <>
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <DetailHeaderInfo
          title={name}
          titleFallback="Template"
          type="template"
          description={context ?? undefined}
          createdAt={createdAt}
          updatedAt={updatedAt}
          descriptionPlaceholder="Add context or guidance"
          emptyDescriptionLabel="No context provided"
          titleAddon={
            isReadOnly ? null : (
              <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit title and context"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit template</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="tpl-title">Title</Label>
                      <Input
                        id="tpl-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Template title"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="tpl-desc">Context</Label>
                      <Textarea
                        id="tpl-desc"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        placeholder="Add context or guidance"
                        rows={4}
                      />
                    </div>
                  </div>
                  {saveError && (
                    <p role="alert" className="text-sm text-destructive">
                      {saveError}
                    </p>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => void handleSave()}
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )
          }
        />
        <div className="flex items-center gap-2">
          {createReportTrigger ? (
            <div>{createReportTrigger}</div>
          ) : (
            <DetailHeaderActions
              isLoading={false}
              isPrimaryPending={isCreatingReport}
              primaryLabel="Create report"
              primaryIcon={PlusIcon}
              onPrimary={onCreateReport}
              isPrimaryDisabled={isCreatingReport || !onCreateReport}
              showShare={false}
              showExport={false}
              showDelete={false}
            />
          )}
          {!isReadOnly && (
            <DetailHeaderActions
              isLoading={false}
              isDeleting={isDeletingTemplate}
              onDelete={() => deleteTemplate()}
              showPrimary={false}
              showShare={false}
              showExport={false}
              showDelete
              deleteTitle="Delete template"
              deleteDescription="Are you sure you want to delete this template? This action cannot be undone."
              deleteConfirmLabel="Delete template"
            />
          )}
        </div>
      </div>
      <Separator />
    </>
  );
}
